import type { WorkbenchAction } from "./api/useWorkbenchActions";

export const ANALYST_NOTES_ACTION_ID = "analyst-notes";

/** Actions the Actions popover can run, keyed by backend id. Anything else is hidden. */
export type RunnableActionId =
  | "view-bureau"
  | "print-step5-deal-summary"
  | "mail-contract"
  | "print-funding-fax"
  | "send-funding-fax"
  | "print-cac-vsc-gap-forms";

const RUNNABLE_ACTION_IDS: ReadonlySet<string> = new Set<RunnableActionId>([
  "view-bureau",
  "print-step5-deal-summary",
  "mail-contract",
  "print-funding-fax",
  "send-funding-fax",
  "print-cac-vsc-gap-forms",
]);

/**
 * Whether the id has a handler in the Actions popover.
 * @param {string} id - Backend action id.
 * @returns {boolean} True when runnable.
 */
export function isRunnableActionId(id: string): id is RunnableActionId {
  return RUNNABLE_ACTION_IDS.has(id);
}

/**
 * Actions listed in the Actions popover: everything the role may run except Analyst Notes, which has its own FAB.
 * Actions without a handler are still listed; the popover renders them disabled.
 * @param {WorkbenchAction[] | undefined} actions - Actions returned for the role.
 * @returns {WorkbenchAction[]} Popover actions in backend order.
 */
export function selectPopoverActions(actions: WorkbenchAction[] | undefined): WorkbenchAction[] {
  return (actions ?? []).filter((action) => action.id !== ANALYST_NOTES_ACTION_ID);
}

/**
 * Whether the role may enter analyst notes, i.e. whether the Analyst Notes FAB is shown.
 * @param {WorkbenchAction[] | undefined} actions - Actions returned for the role.
 * @returns {boolean} True when the analyst-notes action is present.
 */
export function hasAnalystNotesAction(actions: WorkbenchAction[] | undefined): boolean {
  return (actions ?? []).some((action) => action.id === ANALYST_NOTES_ACTION_ID);
}
